/**
 * Difficulty + gentle rubber-banding (CLAUDE.md pillar 6).
 *
 * The Cozy↔Spicy dial shapes an encounter end-to-end: the opponent's LEVEL, the
 * AI's competence, how often it bothers to use items, AND a pair of combat
 * multipliers that make a Cozy foe hit softer and fold faster — all applied as
 * gentle multipliers + a level cap + stronger rubber-banding ON TOP of the
 * faithful engine. The real type chart / damage / catch formulas are never gutted;
 * Cozy only ever scales their inputs/outputs in the player's favour.
 *
 * COZY is the decompression contract made literal: a tired player must comfortably
 * win the very first battles with just a starter. So in Cozy specifically —
 *   - the wild is capped at or BELOW the player's lead level (never out-levels you);
 *   - the wild deals REDUCED damage and TAKES more (combat multipliers);
 *   - its AI is passive (rarely picks the smart move) and never uses items;
 *   - catching is easy (a healthy catch-rate boost).
 * The owner reported losing to a wild Charmander — in Cozy that must never happen.
 *
 * MILD is a neutral, faithful baseline. SPICY is a real (but always fair) test:
 * a small level bump, a competent item-using AI, no combat handicap — but the
 * decompression-grade SOFTEN protection still applies so even Spicy can't wall a
 * struggling player.
 *
 * A soft rubber-band keeps things fair:
 *   - if the player's team is UNDER-leveled, the enemy level is pulled DOWN toward
 *     the player (strong, difficulty-independent protection), so a tired player is
 *     never wall-bricked. Cozy softens harder still;
 *   - if the team is OVER-leveled, the enemy level is nudged UP by a *small*,
 *     per-difficulty amount, so there's a touch of challenge without ever spiking.
 *
 * Everything is bounded: the band lives in [-softenCap, challengeCap], the Cozy
 * level cap clamps the foe to the player's lead, and the final level is clamped to
 * a legal [1,100]. Decompression-first means NO level-gating, NO grind wall, and no
 * outcome that punishes the player. The worst case is still a gentle, winnable
 * battle.
 *
 * Pure + deterministic: no rendering/IO and no RNG — this only shapes the numbers
 * a battle is built from; the battle engine threads its own injected Rng.
 */
import type { Difficulty } from '@/shared';

/** The per-difficulty tuning knobs the dial maps to. */
export interface DifficultyParams {
  /** Multiplier on the base encounter level (cozy < mild ≤ spicy). */
  readonly levelScalar: number;
  /** Probability the AI plays the "smart" branch each turn (0–1). */
  readonly aiQuality: number;
  /** Probability the AI uses a healing/buff item when it could (0–1). */
  readonly itemUseChance: number;
  /** Fraction of an over-level gap converted into added challenge. */
  readonly challengeFraction: number;
  /** Hard cap on how many levels the rubber-band may ADD when over-leveled. */
  readonly challengeCap: number;
  /** Fraction of an UNDER-level gap turned into softening (before the soften cap). */
  readonly softenFraction: number;
  /** Max levels the rubber-band may SUBTRACT to protect an under-leveled player. */
  readonly softenCap: number;
  /**
   * Cap on the foe's level RELATIVE to the player's lead/team level. The foe's
   * scaled level is clamped to `playerLevel + enemyLevelOffset`. Cozy uses a
   * *negative* offset (the wild is at or BELOW the player's lead — it never
   * out-levels you). Mild/Spicy allow a small headroom so the band can still add
   * its gentle challenge. `Infinity` would disable the cap entirely.
   */
  readonly enemyLevelOffset: number;
  /**
   * Multiplier on damage the FOE (opponent side) deals to the player. Cozy < 1
   * (the wild hits softer); Mild/Spicy = 1 (faithful). Applied via the engine's
   * existing damage-multiplier hook, never by editing the damage formula.
   */
  readonly foeDamageMultiplier: number;
  /**
   * Multiplier on damage the PLAYER deals to the foe. Cozy > 1 (the wild takes
   * more, so a starter folds it fast); Mild/Spicy = 1 (faithful).
   */
  readonly playerDamageMultiplier: number;
  /**
   * Multiplier on the species capture rate fed into the (unchanged) catch formula.
   * ALL difficulties ease catching (owner direction: "get all sorts of Pokémon"
   * reliably), ordered cozy > mild > spicy, all > 1. The catch maths and its
   * 255-guaranteed ceiling are untouched — we only ease the input — so weakened
   * commons/rares stick comfortably while low-capture-rate legendaries stay hard.
   */
  readonly catchRateMultiplier: number;
}

/** The frozen Cozy→Spicy tuning table. */
export const DIFFICULTIES: Readonly<Record<Difficulty, DifficultyParams>> = {
  cozy: {
    // Wilds arrive a notch under their band, and are then hard-capped to the
    // player's lead minus a level (see enemyLevelOffset) — so a starter always
    // meets a same-or-lower foe.
    levelScalar: 0.85,
    // Near-passive: the foe almost always flails at a random move.
    aiQuality: 0.05,
    itemUseChance: 0.0,
    // Barely any over-level bump; soften hard and deep.
    challengeFraction: 0.05,
    challengeCap: 0,
    softenFraction: 0.9,
    softenCap: 12,
    // The wild is at or BELOW the player's lead level — never above it.
    enemyLevelOffset: -1,
    // The wild hits softer and folds faster; catching is easy.
    // A high catch-rate ease so a tired player RELIABLY catches "all sorts of
    // Pokémon": once a wild is weakened at all, common/rare capture rates blow
    // past the 255-ceiling and the ball holds. Legendaries (capture rate ~3)
    // still stay hard — 3×6 = 18, far under the ceiling — so they remain a
    // memorable hush, never a trivial tick.
    foeDamageMultiplier: 0.55,
    playerDamageMultiplier: 1.4,
    catchRateMultiplier: 6.0,
  },
  mild: {
    levelScalar: 1.0,
    aiQuality: 0.55,
    itemUseChance: 0.15,
    challengeFraction: 0.2,
    challengeCap: 2,
    softenFraction: 0.6,
    softenCap: 6,
    // A little headroom so the gentle over-level bump can still land.
    enemyLevelOffset: 3,
    // Mild keeps faithful COMBAT (damage in/out = 1) but still eases CATCHING —
    // the owner wants "all sorts of Pokémon" reliably even on the neutral dial.
    // 2.5× makes weakened commons/rares stick comfortably; legendaries stay hard.
    foeDamageMultiplier: 1.0,
    playerDamageMultiplier: 1.0,
    catchRateMultiplier: 2.5,
  },
  spicy: {
    levelScalar: 1.08,
    aiQuality: 0.9,
    itemUseChance: 0.35,
    challengeFraction: 0.3,
    challengeCap: 3,
    // Even Spicy keeps the decompression-grade soften floor — never a wall.
    softenFraction: 0.6,
    softenCap: 5,
    enemyLevelOffset: 6,
    // Spicy keeps faithful combat, and only a SMALL catch ease (1.5×) so capture
    // is still the least forgiving here — rares take a little real work — while
    // never being pure frustration. Legendaries remain the hardest of all.
    foeDamageMultiplier: 1.0,
    playerDamageMultiplier: 1.0,
    catchRateMultiplier: 1.5,
  },
};

/**
 * Max levels the rubber-band may SUBTRACT to protect an under-leveled player, by
 * difficulty. Cozy softens deepest; every difficulty keeps a strong floor because
 * protection is the decompression contract — even Spicy never lets a struggling
 * player get crushed.
 */
export const SOFTEN_CAP: Readonly<Record<Difficulty, number>> = {
  cozy: DIFFICULTIES.cozy.softenCap,
  mild: DIFFICULTIES.mild.softenCap,
  spicy: DIFFICULTIES.spicy.softenCap,
};

/** Per-difficulty cap on the challenge the rubber-band may add (no spikes). */
export const CHALLENGE_CAP: Readonly<Record<Difficulty, number>> = {
  cozy: DIFFICULTIES.cozy.challengeCap,
  mild: DIFFICULTIES.mild.challengeCap,
  spicy: DIFFICULTIES.spicy.challengeCap,
};

/** The result of scaling one encounter for the current difficulty + rubber-band. */
export interface ScaledEncounter {
  /** The opponent level to build the wild/trainer mon at (clamped to [1,100]). */
  readonly enemyLevel: number;
  /** Probability the AI plays its smart branch (0–1). */
  readonly aiQuality: number;
  /** Probability the AI uses an item when it could (0–1). */
  readonly itemUseChance: number;
  /** Multiplier on damage the FOE deals (≤ 1 in Cozy, 1 otherwise). */
  readonly foeDamageMultiplier: number;
  /** Multiplier on damage the PLAYER deals (≥ 1 in Cozy, 1 otherwise). */
  readonly playerDamageMultiplier: number;
  /** Multiplier on the species capture rate fed to the catch formula. */
  readonly catchRateMultiplier: number;
}

/** Scalar applied to opponent levels/stats for a difficulty (cozy < mild < spicy). */
export function difficultyScalar(difficulty: Difficulty): number {
  return DIFFICULTIES[difficulty].levelScalar;
}

/** Multiplier on damage the FOE deals to the player for a difficulty (≤ 1). */
export function foeDamageMultiplier(difficulty: Difficulty): number {
  return DIFFICULTIES[difficulty].foeDamageMultiplier;
}

/** Multiplier on damage the PLAYER deals to the foe for a difficulty (≥ 1). */
export function playerDamageMultiplier(difficulty: Difficulty): number {
  return DIFFICULTIES[difficulty].playerDamageMultiplier;
}

/** Multiplier on the species capture rate for a difficulty (> 1 on all; easiest in Cozy). */
export function catchRateMultiplier(difficulty: Difficulty): number {
  return DIFFICULTIES[difficulty].catchRateMultiplier;
}

/**
 * The hard cap on a foe's level given the player's lead/team level, by difficulty.
 * In Cozy this is `playerLevel - 1` (the foe is at or below your lead); Mild/Spicy
 * allow a little headroom so the gentle over-level bump can still land.
 */
export function enemyLevelCap(difficulty: Difficulty, playerLevel: number): number {
  return playerLevel + DIFFICULTIES[difficulty].enemyLevelOffset;
}

/**
 * Gentle rubber-band: a bounded LEVEL DELTA that nudges an encounter toward the
 * player's team so it stays fair. Negative ⇒ softer (player under-leveled);
 * positive ⇒ a mild bump (player over-leveled). Never punishes; only softens
 * spikes. Result ∈ [-softenCap, challengeCap] for the difficulty.
 */
export function rubberBand(
  difficulty: Difficulty,
  playerAverageLevel: number,
  baseEncounterLevel: number,
): number {
  const params = DIFFICULTIES[difficulty];
  const gap = playerAverageLevel - baseEncounterLevel;
  if (gap === 0) return 0;

  if (gap < 0) {
    // Under-leveled: pull the encounter DOWN toward the player, strongly but
    // capped, so it never becomes a wall. Cozy softens the hardest.
    const soften = Math.round(gap * params.softenFraction); // negative
    return Math.max(-params.softenCap, soften);
  }

  // Over-leveled: add a small, per-difficulty bump — bounded so it can't spike.
  const challenge = Math.round(gap * params.challengeFraction); // positive
  return Math.min(params.challengeCap, challenge);
}

/**
 * Scale one encounter for the active difficulty, applying the rubber-band AND the
 * per-difficulty cap that keeps the foe at/under the player's lead (Cozy) or within
 * a small headroom (Mild/Spicy).
 *
 * `playerTeamStrength` is the player's effective average/lead team level; `baseLevel`
 * is the route/encounter's intended level before any adjustment. The AI + combat
 * dials come from difficulty alone — the rubber-band + cap only ever touch the
 * LEVEL, keeping the decompression contract intact.
 */
export function scaleEncounter(
  difficulty: Difficulty,
  playerTeamStrength: number,
  baseLevel: number,
): ScaledEncounter {
  const params = DIFFICULTIES[difficulty];
  const scaled = Math.round(baseLevel * params.levelScalar);
  const band = rubberBand(difficulty, playerTeamStrength, baseLevel);
  const banded = scaled + band;
  // The relative cap is decisive in Cozy (foe ≤ player lead − 1); it only ever
  // pulls the level DOWN (Math.min) so it can never make a battle harder.
  const capped = Math.min(banded, enemyLevelCap(difficulty, playerTeamStrength));
  const enemyLevel = clampLevel(capped);

  return {
    enemyLevel,
    aiQuality: params.aiQuality,
    itemUseChance: params.itemUseChance,
    foeDamageMultiplier: params.foeDamageMultiplier,
    playerDamageMultiplier: params.playerDamageMultiplier,
    catchRateMultiplier: params.catchRateMultiplier,
  };
}

/** Clamp a level into the legal [1, 100] band. */
function clampLevel(level: number): number {
  if (level < 1) return 1;
  if (level > 100) return 100;
  return level;
}
